import { ProductInventory, WarehouseInventory } from "@domain/inventory/models/Inventory";
import { ProductInventoryDTO, WarehouseInventoryDTO, } from "../dto/InventoryDTO";
import { ProductMapper } from "../../product/mappers/ProductMapper";
import { PurchaseOrderItem, PurchaseOrderItemStatus } from "@domain/purchasing";
import { PurchaseOrderItemDTO } from "@infrastructure/purchasing/dto/PurchaseOrderDTO";
import { WarehouseMapper } from "./WarehouseMapper";
import { TransportMethod } from "@domain/shipment";
import { TransportMethodDTO } from "@infrastructure/product/dto/TransportMethodDTO";
import { ForecastPerWeekMapper } from "./ForecastPerWeekMapper";

export class InventoryMapper {
    static warehouseInventoryToDomain(dto: WarehouseInventoryDTO): WarehouseInventory {
        return {
            warehouseId: dto.warehouseId,
            product: ProductMapper.toModel(dto.product),
            quantity: dto.quantity
        };
    }

    private static transportMethodToModel(method: TransportMethodDTO): TransportMethod {
        switch (method) {
            case TransportMethodDTO.TRUCK:
                return TransportMethod.TRUCK;
            case TransportMethodDTO.BOAT:
                return TransportMethod.BOAT;
            case TransportMethodDTO.PLANE:
                return TransportMethod.PLANE;
            case TransportMethodDTO.TRAIN:
                return TransportMethod.TRAIN;
            default:
                return TransportMethod.TRUCK;
        }
    }

    static purchaseOrderItemToDomain(dto: PurchaseOrderItemDTO): Omit<PurchaseOrderItem, 'product'> {
        return {
            id: dto.id,
            quantity: dto.quantity,
            status: dto.status as PurchaseOrderItemStatus,
            eta: dto.eta ? new Date(dto.eta) : undefined,
            shipment: dto.shipment ? {
                id: dto.shipment.id,
                companyId: dto.shipment.companyId,
                name: dto.shipment.name,
                arrivalDate: new Date(dto.shipment.arrivalDate),
                departureDate: new Date(dto.shipment.departureDate),
                processedDate: new Date(dto.shipment.processedDate),
                transportMethod: InventoryMapper.transportMethodToModel(dto.shipment.transportMethod),
                destinationWarehouse: dto.shipment.destinationWarehouse ? WarehouseMapper.toModel(dto.shipment.destinationWarehouse) : undefined,
                purchaseOrderItems: [], // Add empty array for missing property
                createdAt: new Date(dto.shipment.createdAt),
                updatedAt: new Date(dto.shipment.updatedAt)
            } : undefined
        };
    }

    static productInventoryToDomain(dto: ProductInventoryDTO): ProductInventory {
        return {
            product: ProductMapper.toModel(dto.product),
            quantity: dto.quantity,
            warehouses: dto.warehouses,
            leadTime: dto.leadTime,
            requiredOverstock: dto.requiredOverstock,
            weeksOfStockLeft: dto.weeksOfStockLeft,
            orderWithinDays: dto.orderWithinDays,
            weeksForecasted: dto.weeksForecasted,
            purchaseOrderItems: dto.purchaseOrderItems?.map(item => InventoryMapper.purchaseOrderItemToDomain(item)),
            forecastPerWeek: dto.forecastPerWeek?.map(forecast => ForecastPerWeekMapper.toModel(forecast)),  
        };
    }

    static toDTO(inventory: WarehouseInventory): WarehouseInventoryDTO {
        return {
            product: ProductMapper.toCreateDTO(inventory.product),
            quantity: inventory.quantity,
            warehouseId: inventory.warehouseId
        };
    }
}